import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Tag,
  Space,
  Table,
  Spin,
  Segmented,
  Tooltip,
  Upload,
  Modal,
  Descriptions,
  theme,
} from "antd";
import {
  SaveOutlined,
  CopyOutlined,
  MailOutlined,
  GlobalOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  UserOutlined,
  IdcardOutlined,
  PictureOutlined,
  FileProtectOutlined,
  PlusOutlined,
  LinkOutlined,
  EditOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { PageLayout, PageHeader, SectionCard } from "../../Common/Layout";
import { adminContext } from "../../Common/PageRoute";
import PracticeDataRequestService, {
  CsvTemplateType,
  CsvTemplateOptions,
  getEnumTypeValue,
  PortalUrlType,
  EmailProviderType,
  UserCreationMethod,
  LoginMode,
  IValidationResult,
} from "../../Services/PracticeDataRequestService";
import { Utils } from "../../Utilities/Utils";
import CsvValidationModal from "./CsvValidationModal";

const { Option } = Select;
const { Dragger } = Upload;

interface DataConversionFile {
  Name: string;
  FileUrl: string;
  Type: CsvTemplateType;
}

interface PracticeDataRequestFormValues {
  practiceName?: string;
  practicePhoneNumber?: string;
  publicEmailNoReply?: string;
  website?: string;
  proposedUrl?: string;
  contactPersonName?: string;
  contactPersonEmail?: string;
  contactPersonPhone?: string;
  clientEmail?: string;
  logoUrl?: string;
  faviconUrl?: string;
  invoiceSampleHeaderUrl?: string;
  invoiceSampleFooterUrl?: string;
  status?: number;
  dataConversionFiles?: DataConversionFile[];
}
interface DetailFieldProps {
  label: string;
  name: string;
  isEditing: boolean;
  children: React.ReactNode;
  value?: string;
  required?: boolean;
  emailRule?: boolean;
  displayPrefix?: string;
  isLink?: boolean;
}
// Component to display a detail field, either in view or edit mode
const DetailField: React.FC<DetailFieldProps> = ({
  label,
  name,
  isEditing,
  children,
  value,
  required,
  emailRule,
  displayPrefix,
  isLink,
}) => {
  const { token } = theme.useToken();
  if (!isEditing) {
    const displayValue = value ? `${displayPrefix || ""}${value}` : "";
    return (
      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.3,
            textTransform: "uppercase",
            color: token.colorTextSecondary,
            marginBottom: 6,
          }}
        >
          {label}
        </div>
        <div
          style={{
            padding: "8px 12px",
            background: token.colorFillAlter,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadiusSM,
            fontSize: 13,
            color: token.colorText,
            fontWeight: 500,
            wordBreak: "break-word",
          }}
        >
          {displayValue ? (
            isLink ? (
              <a href={displayValue} target="_blank" rel="noreferrer">
                {displayValue}
              </a>
            ) : (
              displayValue
            )
          ) : (
            <span style={{ color: token.colorTextQuaternary, fontWeight: 400 }}>
              {" "}
              Not set
            </span>
          )}
        </div>
      </div>
    );
  }

  const rules: any[] = [];
  if (required) rules.push({ required: true, message: `${label} is required` });
  if (emailRule)
    rules.push({ type: "email", message: "Invalid email address" });

  return (
    <Form.Item
      label={label}
      name={name}
      rules={rules.length ? rules : undefined}
    >
      {children}
    </Form.Item>
  );
};
// Function to render tab labels with icons and counts
const renderTabLabel = (
  icon: React.ReactElement,
  text: string,
  active: boolean,
  token: any,
  count?: number,
) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "5px 10px",
    }}
  >
    {React.cloneElement(icon, {
      style: {
        fontSize: 15,
        color: active ? token.colorPrimary : token.colorTextSecondary,
      },
    })}
    <span style={{ fontWeight: active ? 600 : 400 }}>{text}</span>
    {!!count && (
      <span
        style={{
          background: active ? token.colorPrimary : token.colorFillSecondary,
          color: active ? "#fff" : token.colorTextSecondary,
          fontSize: 11,
          fontWeight: 700,
          borderRadius: 10,
          padding: "0 6px",
          lineHeight: "16px",
          display: "inline-block",
        }}
      >
        {" "}
        {count}{" "}
      </span>
    )}
  </div>
);

const normalizeFile = (item: any) => {
  if (!item)
    return { name: "", type: CsvTemplateType.Other, file: null, rawFile: null };
  const rawType =
    item.type !== undefined
      ? item.type
      : item.Type !== undefined
        ? item.Type
        : item.importType;
  const enumType = getEnumTypeValue(rawType);
  const fileName =
    item.name ||
    item.Name ||
    item.file?.name ||
    item.File?.Name ||
    item.File?.name ||
    (item.rawFile as File)?.name ||
    "";

  const rawFileObj =
    item.file ||
    item.File ||
    (item.fileUrl || item.FileUrl
      ? { path: item.fileUrl || item.FileUrl, name: fileName }
      : null);

  const fileObj = rawFileObj
    ? {
        id: rawFileObj.id || rawFileObj.Id,
        name: rawFileObj.name || rawFileObj.Name || fileName,
        path: rawFileObj.path || rawFileObj.Path || "",
        contentType: rawFileObj.contentType || rawFileObj.ContentType,
        length: rawFileObj.length ?? rawFileObj.Length,
      }
    : null;

  const rawF =
    item.rawFile ||
    item.originFileObj ||
    (item.file instanceof File ? item.file : null);

  return {
    name: fileName,
    file: fileObj,
    type: enumType,
    subType:
      item?.SubType ??
      item?.subType ??
      item?.EmailTemplateSubType ??
      item?.emailTemplateSubType,
    emailTemplateSubType:
      item?.EmailTemplateSubType ??
      item?.emailTemplateSubType ??
      item?.SubType ??
      item?.subType,
    rawFile: rawF,
  };
};

const sortDataConversionFiles = (files: any[]) => {
  const optionsOrder = CsvTemplateOptions.map((opt) => opt.value);
  return [...(files || [])].map(normalizeFile).sort((a, b) => {
    const idxA = optionsOrder.indexOf(a.type as any);
    const idxB = optionsOrder.indexOf(b.type as any);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });
};

const PracticeDataRequestDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { openNotification } = React.useContext(adminContext);
  const { token } = theme.useToken();
  const [form] = Form.useForm<PracticeDataRequestFormValues>();
  const [saving, setSaving] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>("basic");
  const [addFileModal, setAddFileModal] = useState<boolean>(false);
  const [newFileType, setNewFileType] = useState<CsvTemplateType>(
    CsvTemplateOptions[0].value,
  );
  const [newFileName, setNewFileName] = useState<string>("");
  const [newFileUploading, setNewFileUploading] = useState<boolean>(false);
  const dataConversionFiles = Form.useWatch("dataConversionFiles", form) || [];
  const [rawAssetFiles, setRawAssetFiles] = useState<{ [key: string]: File }>(
    {},
  );
  const [validationModalOpen, setValidationModalOpen] =
    useState<boolean>(false);
  const [activeValidationResult, setActiveValidationResult] =
    useState<IValidationResult | null>(null);

  const getUrlString = (val: any) =>
    typeof val === "string" ? val : val?.path || val?.Path || "";

  const getAdminAssetPreviewUrl = (val: any, assetKey: string) => {
    if (!val) return "";
    const raw = typeof val === "string" ? val : val?.path || val?.Path || "";
    if (!raw || raw.trim() === "") return "";
    if (raw.startsWith("blob:") || raw.startsWith("data:")) return raw;
    if (raw.includes("AO_Header.jpg") || raw.includes("AO_Footer.jpg")) {
      return raw.startsWith("http")
        ? raw
        : `${window.location.origin}/${raw.replace(/^\//, "")}`;
    }
    const currentId = id || requestData?.id || requestData?.Id;
    if (currentId) {
      return `/api/PracticeDataRequests/${currentId}/Asset/${assetKey}?v=${encodeURIComponent(raw.split("/").pop() || "")}`;
    }
    return raw;
  };

  const getStatusValue = (status: any): number => {
    if (typeof status === "number") return status;
    if (status === "Submitted") return 2;
    if (status === "Processed") return 3;
    return 1;
  };

  const applyRequestDataToForm = (item: any) => {
    form.setFieldsValue({
      ...item,
      practicePhoneNumber: Utils.formatUkPhoneNumberForInput(
        item.practicePhoneNumber,
      ),
      contactPersonPhone: Utils.formatUkPhoneNumberForInput(
        item.contactPersonPhone,
      ),
      logoUrl: getUrlString(item.logoUrl || item.LogoUrl),
      faviconUrl: getUrlString(item.faviconUrl || item.FaviconUrl),
      invoiceSampleHeaderUrl: getUrlString(
        item.invoiceSampleHeaderUrl || item.InvoiceSampleHeaderUrl,
      ),
      invoiceSampleFooterUrl: getUrlString(
        item.invoiceSampleFooterUrl || item.InvoiceSampleFooterUrl,
      ),
      status: getStatusValue(item.status),
      dataConversionFiles: sortDataConversionFiles(
        item.dataConversionFiles || item.DataConversionFiles,
      ),
    });
  };

  // Fetch request details from the API
  const fetchRequestDetails = async () => {
    if (!id) return;
    try {
      const res =
        await PracticeDataRequestService.getPracticeDataRequestById(id);
      if (res.status && res.result) {
        const item = res.result;
        setRequestData(item);
        applyRequestDataToForm(item);

        if (typeof window !== "undefined") {
          (window as any).dynamicPathLabels =
            (window as any).dynamicPathLabels || {};
          (window as any).dynamicPathLabels[id] =
            item.practiceName ||
            item.referenceNumber ||
            "Practice Data Request";
          window.dispatchEvent(new CustomEvent("dynamicPathLabelsUpdate"));
        }
      } else {
        openNotification(
          "error",
          res.message || "Failed to load practice data request.",
        );
      }
    } catch (err: any) {
      openNotification(
        "error",
        err?.message || "Error loading request details.",
      );
    }
  };

  useEffect(() => {
    fetchRequestDetails();

    return () => {
      if (
        id &&
        typeof window !== "undefined" &&
        (window as any).dynamicPathLabels
      ) {
        delete (window as any).dynamicPathLabels[id];
        window.dispatchEvent(new CustomEvent("dynamicPathLabelsUpdate"));
      }
    };
  }, [id]);

  const enterEditMode = () => setIsEditing(true);
  const cancelEditMode = () => {
    if (requestData) applyRequestDataToForm(requestData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const sortedFiles = sortDataConversionFiles(
        values.dataConversionFiles || [],
      );
      const rawFilesToUpload: File[] = [];

      const cleanFiles = sortedFiles.map((f: any) => {
        if (f.rawFile instanceof File) {
          rawFilesToUpload.push(f.rawFile);
        }
        const subTypeVal =
          f.subType ||
          f.SubType ||
          f.emailTemplateSubType ||
          f.EmailTemplateSubType ||
          null;
        return {
          name: f.name,
          type: getEnumTypeValue(f.type),
          subType: subTypeVal,
          emailTemplateSubType: subTypeVal,
          file:
            f.file?.path && !f.file.path.startsWith("blob:") ? f.file : null,
        };
      });

      const sanitizeAsset = (val: any) => {
        if (!val) return null;
        if (typeof val === "string") {
          if (val.startsWith("blob:")) return null;
          return {
            name: val.split("/").pop() || "file",
            path: val,
          };
        }
        if (typeof val === "object") {
          const path = val.path || val.Path || "";
          const isBlob = path.startsWith("blob:");
          return {
            id: val.id || val.Id,
            name: val.name || val.Name || "",
            contentType: val.contentType || val.ContentType,
            length: val.length ?? val.Length,
            path: isBlob ? "" : path,
          };
        }
        return val;
      };

      const payload = {
        ...requestData,
        ...values,
        id: id,
        practicePhoneNumber: Utils.normalizeUkPhoneNumberToSave(
          values.practicePhoneNumber,
        ),
        contactPersonPhone: Utils.normalizeUkPhoneNumberToSave(
          values.contactPersonPhone,
        ),
        logoUrl: sanitizeAsset(values.logoUrl),
        faviconUrl: sanitizeAsset(values.faviconUrl),
        invoiceSampleHeaderUrl: sanitizeAsset(values.invoiceSampleHeaderUrl),
        invoiceSampleFooterUrl: sanitizeAsset(values.invoiceSampleFooterUrl),
        dataConversionFiles: cleanFiles,
      };

      let submitPayload: any = payload;
      const hasAssetFiles = Object.keys(rawAssetFiles).length > 0;
      if (rawFilesToUpload.length > 0 || hasAssetFiles) {
        const formData = new FormData();
        formData.append("data", JSON.stringify(payload));
        rawFilesToUpload.forEach((rf) => {
          formData.append("files", rf, rf.name);
        });
        Object.keys(rawAssetFiles).forEach((k) => {
          if (rawAssetFiles[k]) {
            formData.append(k, rawAssetFiles[k], rawAssetFiles[k].name);
          }
        });
        submitPayload = formData;
      }

      const res =
        await PracticeDataRequestService.updatePracticeDataRequest(
          submitPayload,
        );
      if (res.status && res.result) {
        setRawAssetFiles({});
        openNotification(
          "success",
          "Practice data request updated successfully!",
        );
        setRequestData(res.result);

        const updatedSortedFiles = sortDataConversionFiles(
          res.result.dataConversionFiles ||
            res.result.DataConversionFiles ||
            [],
        );
        form.setFieldsValue({
          ...res.result,
          practicePhoneNumber: Utils.formatUkPhoneNumberForInput(
            res.result.practicePhoneNumber,
          ),
          contactPersonPhone: Utils.formatUkPhoneNumberForInput(
            res.result.contactPersonPhone,
          ),
          logoUrl: getUrlString(res.result.logoUrl || res.result.LogoUrl),
          faviconUrl: getUrlString(
            res.result.faviconUrl || res.result.FaviconUrl,
          ),
          invoiceSampleHeaderUrl: getUrlString(
            res.result.invoiceSampleHeaderUrl ||
              res.result.InvoiceSampleHeaderUrl,
          ),
          invoiceSampleFooterUrl: getUrlString(
            res.result.invoiceSampleFooterUrl ||
              res.result.InvoiceSampleFooterUrl,
          ),
          dataConversionFiles: updatedSortedFiles,
        });

        if (typeof window !== "undefined") {
          (window as any).dynamicPathLabels =
            (window as any).dynamicPathLabels || {};
          (window as any).dynamicPathLabels[id!] =
            res.result.practiceName ||
            res.result.referenceNumber ||
            "Practice Data Request";
          window.dispatchEvent(new CustomEvent("dynamicPathLabelsUpdate"));
        }
      } else {
        openNotification("error", res.message || "Failed to update request.");
      }
    } catch (err: any) {
      if (err?.errorFields) {
        openNotification("warning", "Please fill in all required fields.");
      } else {
        openNotification("error", err?.message || "Failed to save changes.");
      }
    } finally {
      setSaving(false);
    }
  };

  // Copy public client form link to clipboard
  const handleCopyLink = () => {
    if (!requestData?.token) return;
    const link = `${window.location.origin}/data-request/${requestData.token}`;
    navigator.clipboard.writeText(link);
    openNotification(
      "success",
      "Public data request link copied to clipboard!",
    );
  };

  // Resend reminder email
  const handleResendEmail = async () => {
    if (!id) return;
    try {
      const res = await PracticeDataRequestService.resendEmail(id);
      if (res.status) {
        openNotification("success", "Reminder email sent successfully!");
      } else {
        openNotification("error", res.message || "Failed to send email");
      }
    } catch {
      openNotification("error", "Error sending reminder email.");
    }
  };

  // Handle file upload for assets (logo, favicon, etc.)
  const handleAssetUpload = async (
    field: keyof PracticeDataRequestFormValues,
    file: File,
  ) => {
    try {
      setRawAssetFiles((prev) => ({ ...prev, [field]: file }));
      form.setFieldValue(field, {
        id: id,
        name: file.name,
        contentType: file.type || "application/octet-stream",
        length: file.size,
        path: URL.createObjectURL(file),
      });
      openNotification(
        "success",
        "Asset attached! Click Save Changes to save.",
      );
    } catch {
      openNotification("error", "Error attaching file.");
    }
  };
  // Handle adding a new data conversion file
  const handleAddNewConversionFile = async (file: File) => {
    setNewFileUploading(true);
    try {
      const currentFiles = form.getFieldValue("dataConversionFiles") || [];
      const fileName = newFileName.trim() || file.name || newFileType;
      const fileType = getEnumTypeValue(newFileType);
      const updatedFiles = sortDataConversionFiles([
        ...currentFiles,
        {
          name: fileName,
          type: fileType,
          file: {
            name: file.name,
            path: URL.createObjectURL(file),
          },
          rawFile: file,
        },
      ]);
      form.setFieldsValue({ dataConversionFiles: updatedFiles as any });
      setNewFileName("");
      setAddFileModal(false);
      openNotification(
        "success",
        `${newFileType} file attached. Click Save Changes to save.`,
      );
    } catch {
      openNotification("error", "Error attaching file.");
    } finally {
      setNewFileUploading(false);
    }
  };
  // Downloading CSV template
  const handleDownloadCsvTemplate = (templateType?: string) => {
    const type = templateType || CsvTemplateOptions?.[0]?.value;
    if (!type) {
      openNotification("warning", "Please select a template first.");
      return;
    }
    if (type === CsvTemplateType.Other) {
      return;
    }
    PracticeDataRequestService.downloadCsvTemplate(type as CsvTemplateType);
  };

  // Status
  const getStatusBadge = (status: any) => {
    const s =
      typeof status === "number"
        ? ["Unknown", "Pending", "Submitted", "Processed"][status]
        : status;
    if (s === "Pending" || status === 1) {
      return (
        <Tag
          color="orange"
          icon={<ClockCircleOutlined />}
          style={{ borderRadius: 12, padding: "2px 10px", fontWeight: 600 }}
        >
          Pending
        </Tag>
      );
    }
    if (s === "Submitted" || status === 2) {
      return (
        <Tag
          color="green"
          icon={<CheckCircleOutlined />}
          style={{
            borderRadius: 12,
            padding: "2px 10px",
            fontWeight: 500,
            color: "#008000",
          }}
        >
          {" "}
          Submitted
        </Tag>
      );
    }
  };
  const titleText =
    requestData?.practiceName ||
    requestData?.referenceNumber ||
    "Practice Data Request";
  const refText = requestData?.referenceNumber || "-";
  const createdDateText = Utils.getFormattedDate(
    requestData?.createdBy?.date || requestData?.createdAt,
  );
  const filesCount = dataConversionFiles.length;

  // Header action buttons
  const headerActions = [
    requestData?.token && (
      <Tooltip title="Copy Public Client Form Link" key="copy">
        <Button
          icon={<CopyOutlined />}
          onClick={handleCopyLink}
          size="middle"
          className="form_btn"
        >
          {" "}
          Copy Link
        </Button>
      </Tooltip>
    ),
    requestData?.token && (
      <Tooltip title="Open Public Client Form in New Tab" key="open">
        <Button
          icon={<GlobalOutlined />}
          href={`/data-request/${requestData.token}`}
          target="_blank"
          rel="noopener noreferrer"
          size="middle"
          className="form_btn"
        >
          Open Form{" "}
        </Button>
      </Tooltip>
    ),
    <Button
      key="resend"
      icon={<MailOutlined />}
      onClick={handleResendEmail}
      size="middle"
      className="form_btn"
    >
      {" "}
      Resend Email
    </Button>,
  ];
  if (!isEditing) {
    headerActions.push(
      <Button
        key="edit"
        type="primary"
        icon={<EditOutlined />}
        onClick={enterEditMode}
        size="middle"
        className="form_btn_save"
      >
        {" "}
        Edit Details
      </Button>,
    );
  } else {
    headerActions.push(
      <Button
        key="cancel"
        icon={<CloseOutlined />}
        onClick={cancelEditMode}
        size="middle"
        className="form_btn"
      >
        {" "}
        Cancel
      </Button>,
      <Button
        key="save"
        type="primary"
        icon={<SaveOutlined />}
        loading={saving}
        onClick={handleSave}
        size="middle"
        className="form_btn_save"
      >
        {" "}
        Save Changes{" "}
      </Button>,
    );
  }

  // Render the main component
  return (
    <PageLayout>
      <PageHeader
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{ fontSize: 18, fontWeight: 700, color: token.colorText }}
            >
              {titleText}
            </span>{" "}
            {getStatusBadge(requestData?.status)}
          </div>
        }
        subtitle={
          <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
            Reference Number:{" "}
            <strong style={{ color: token.colorText }}>{refText}</strong> •
            Created: {createdDateText} • Client Email:{" "}
            {requestData?.clientEmail || "-"}
          </span>
        }
        actions={headerActions}
      />

      <div style={{ marginTop: 16 }}>
        <div style={{ marginBottom: 16, overflowX: "auto" }}>
          <Segmented
            value={activeTab}
            onChange={(val) => setActiveTab(val as string)}
            size="middle"
            options={[
              {
                value: "basic",
                label: renderTabLabel(
                  <IdcardOutlined />,
                  "Practice & Contact Info",
                  activeTab === "basic",
                  token,
                ),
              },
              {
                value: "branding",
                label: renderTabLabel(
                  <PictureOutlined />,
                  "Branding Assets",
                  activeTab === "branding",
                  token,
                ),
              },
              {
                value: "invoices",
                label: renderTabLabel(
                  <FileProtectOutlined />,
                  "Invoice Templates",
                  activeTab === "invoices",
                  token,
                ),
              },
              {
                value: "files",
                label: renderTabLabel(
                  <FileTextOutlined />,
                  "Uploaded Data & Files",
                  activeTab === "files",
                  token,
                  filesCount,
                ),
              },
            ]}
          />
        </div>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: 1 }}
          disabled={!isEditing}
        >
          {/* TAB 1: Practice & Contact Info */}
          <div style={{ display: activeTab === "basic" ? "block" : "none" }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <SectionCard
                  title="Practice Information"
                  description="Workspace branding and general contact details"
                >
                  {!isEditing ? (
                    <Descriptions
                      bordered
                      column={1}
                      size="middle"
                      labelStyle={{
                        width: 220,
                        fontWeight: 500,
                        color: token.colorTextSecondary,
                        background: token.colorFillAlter,
                      }}
                      contentStyle={{
                        color: token.colorText,
                        fontWeight: 500,
                      }}
                    >
                      <Descriptions.Item label="Practice Name">
                        {requestData?.practiceName || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Practice Phone Number">
                        {requestData?.practicePhoneNumber || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Email">
                        {requestData?.publicEmailNoReply || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Website">
                        {requestData?.website ? (
                          <span>{requestData.website}</span>
                        ) : (
                          "—"
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Proposed Workspace URL">
                        {requestData?.proposedUrl ? (
                          <span>{requestData.proposedUrl}</span>
                        ) : (
                          "—"
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Row gutter={12}>
                      <Col span={24}>
                        <Form.Item
                          label="Practice Name"
                          name="practiceName"
                          rules={[
                            {
                              required: true,
                              message: "Practice Name is required",
                            },
                          ]}
                        >
                          <Input placeholder="Enter practice name" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Phone number"
                          name="practicePhoneNumber"
                          initialValue="+44"
                          rules={[
                            {
                              required: true,
                              message: "Phone number is required",
                            },
                            {
                              validator: Utils.ValidateUkPhoneNumber,
                            },
                          ]}
                          getValueFromEvent={(e: any) => {
                            const raw = e?.target ? e.target.value : e;
                            if (typeof raw !== "string") return raw;
                            let cleaned = raw.replace(/[^\d\s]/g, "");
                            const pure = cleaned.replace(/\s/g, "");
                            if (pure.startsWith("+44")) {
                              cleaned = pure.slice(3);
                            } else if (pure.startsWith("0044")) {
                              cleaned = pure.slice(4);
                            } else if (pure.startsWith("0")) {
                              cleaned = pure.slice(1);
                            }
                            const pureDigits = cleaned.replace(/\s/g, "");
                            if (pureDigits.length > 10) {
                              cleaned = pureDigits.slice(0, 10);
                            }
                            return cleaned;
                          }}
                        >
                          <Input
                            addonBefore="+44"
                            maxLength={10}
                            placeholder="Enter phone number"
                            onKeyPress={(e) => {
                              if (!/[\d\s]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Email"
                          name="publicEmailNoReply"
                          rules={[
                            { required: true, message: "Email is required" },
                          ]}
                        >
                          <Input placeholder="Enter email address" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Website"
                          name="website"
                          rules={[
                            { required: true, message: "Website is required" },
                          ]}
                        >
                          <Input placeholder="Enter website URL" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Proposed Workspace URL"
                          name="proposedUrl"
                          rules={[
                            {
                              required: true,
                              message: "Proposed Workspace URL is required",
                            },
                          ]}
                        >
                          <Input placeholder="Enter proposed workspace URL" />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </SectionCard>
              </Col>

              <Col xs={24} lg={12}>
                <SectionCard
                  title="Users & emails"
                  description="Primary contact, email provider, user creation method & authentication settings"
                  style={{ paddingBottom: isEditing ? 0 : 45 }}
                >
                  {!isEditing ? (
                    <Descriptions
                      bordered
                      column={1}
                      size="middle"
                      labelStyle={{
                        width: 190,
                        fontWeight: 500,
                        color: token.colorTextSecondary,
                        background: token.colorFillAlter,
                      }}
                      contentStyle={{ color: token.colorText, fontWeight: 500 }}
                    >
                      <Descriptions.Item label="Contact Person Name">
                        {requestData?.contactPersonName || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contact Email">
                        {" "}
                        {requestData?.contactPersonEmail || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Contact Phone">
                        {" "}
                        {requestData?.contactPersonPhone || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Client Invite Email">
                        {" "}
                        {requestData?.clientEmail || "—"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Email Provider">
                        {requestData?.emailProvider === 1
                          ? "Microsoft / Outlook"
                          : requestData?.emailProvider === 2
                            ? "Google"
                            : "Other"}
                      </Descriptions.Item>
                      <Descriptions.Item label="User Creation">
                        {requestData?.userCreationMethod === 2
                          ? "Create via SCIM"
                          : "Upload CSV"}
                      </Descriptions.Item>
                      {/* <Descriptions.Item label="Preferred Login Mode">
                        {requestData?.preferredLoginMode === 2
                          ? "SAML"
                          : "User ID & password for all users"}
                      </Descriptions.Item> */}
                    </Descriptions>
                  ) : (
                    <Row gutter={12}>
                      <Col span={24}>
                        <Form.Item
                          label="Contact Person Name"
                          name="contactPersonName"
                          rules={[
                            {
                              required: true,
                              message: "Contact Person Name is required",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter contact person name"
                            prefix={
                              <UserOutlined
                                style={{ color: token.colorTextQuaternary }}
                              />
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Contact Email"
                          name="contactPersonEmail"
                          rules={[
                            {
                              required: true,
                              type: "email",
                              message: "Email is required",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter contact email address"
                            prefix={
                              <MailOutlined
                                style={{ color: token.colorTextQuaternary }}
                              />
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Contact Phone"
                          name="contactPersonPhone"
                          initialValue="+44"
                          rules={[
                            {
                              required: true,
                              message: "Contact phone number is required",
                            },
                            {
                              validator: Utils.ValidateUkPhoneNumber,
                            },
                          ]}
                          getValueFromEvent={(e: any) => {
                            const raw = e?.target ? e.target.value : e;
                            if (typeof raw !== "string") return raw;
                            let cleaned = raw.replace(/[^\d\s]/g, "");
                            const pure = cleaned.replace(/\s/g, "");
                            if (pure.startsWith("+44")) {
                              cleaned = pure.slice(3);
                            } else if (pure.startsWith("0044")) {
                              cleaned = pure.slice(4);
                            } else if (pure.startsWith("0")) {
                              cleaned = pure.slice(1);
                            }
                            const pureDigits = cleaned.replace(/\s/g, "");
                            if (pureDigits.length > 10) {
                              cleaned = pureDigits.slice(0, 10);
                            }
                            return cleaned;
                          }}
                        >
                          <Input
                            addonBefore="+44"
                            maxLength={10}
                            placeholder="Enter contact phone number"
                            onKeyPress={(e) => {
                              if (!/[\d\s]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Client Invite Email"
                          name="clientEmail"
                        >
                          <Input disabled placeholder="Enter email address" />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="Email Provider"
                          name="emailProvider"
                          initialValue={EmailProviderType.MicrosoftOutlook}
                        >
                          <Select>
                            <Option value={EmailProviderType.MicrosoftOutlook}>
                              Microsoft / Outlook
                            </Option>
                            <Option value={EmailProviderType.Google}>
                              Google
                            </Option>
                            <Option value={EmailProviderType.Other}>
                              Other
                            </Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Form.Item
                        shouldUpdate={(prev, curr) =>
                          prev.emailProvider !== curr.emailProvider
                        }
                        noStyle
                      >
                        {({ getFieldValue }) => {
                          const provider = getFieldValue("emailProvider");
                          const isMicrosoft =
                            provider === EmailProviderType.MicrosoftOutlook;
                          return (
                            <>
                              <Col xs={24} sm={12}>
                                <Form.Item
                                  label="User Creation Method"
                                  name="userCreationMethod"
                                  initialValue={UserCreationMethod.UploadCsv}
                                >
                                  <Select>
                                    <Option
                                      value={UserCreationMethod.UploadCsv}
                                    >
                                      Upload CSV
                                    </Option>
                                    <Option
                                      value={UserCreationMethod.CreateViaSCIM}
                                      disabled={!isMicrosoft}
                                    >
                                      Create via SCIM{" "}
                                      {!isMicrosoft ? "(Microsoft only)" : ""}
                                    </Option>
                                  </Select>
                                </Form.Item>
                              </Col>

                              {/* <Col xs={24} sm={12}>
                                <Form.Item
                                  label="Preferred Login Mode"
                                  name="preferredLoginMode"
                                  initialValue={LoginMode.UserIdPassword}
                                >
                                  <Select>
                                    <Option value={LoginMode.UserIdPassword}>
                                      User ID & password for all users
                                    </Option>
                                    <Option
                                      value={LoginMode.SAML}
                                      disabled={!isMicrosoft}
                                    >
                                      SAML{" "}
                                      {!isMicrosoft ? "(Microsoft only)" : ""}
                                    </Option>
                                  </Select>
                                </Form.Item>
                              </Col> */}
                            </>
                          );
                        }}
                      </Form.Item>
                    </Row>
                  )}
                </SectionCard>
              </Col>
            </Row>
          </div>

          {/* TAB 2: Branding Assets */}
          <div style={{ display: activeTab === "branding" ? "block" : "none" }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <SectionCard
                  title="Company Logo"
                  description="Main practice logo displayed in portal and headers"
                >
                  <DetailField
                    label="Logo URL"
                    name="logoUrl"
                    isEditing={isEditing}
                    value={getUrlString(requestData?.logoUrl)}
                    isLink
                  >
                    <Input
                      placeholder="https://..."
                      prefix={
                        <LinkOutlined
                          style={{ color: token.colorTextQuaternary }}
                        />
                      }
                    />
                  </DetailField>

                  <Form.Item
                    shouldUpdate={(prev, curr) => prev.logoUrl !== curr.logoUrl}
                    noStyle
                  >
                    {({ getFieldValue }) => {
                      const logoUrl = getFieldValue("logoUrl");
                      return (
                        <div
                          style={{
                            textAlign: "center",
                            padding: 24,
                            background: token.colorFillAlter,
                            borderRadius: token.borderRadiusLG,
                            border: `1px dashed ${token.colorBorder}`,
                          }}
                        >
                          {logoUrl ? (
                            <div>
                              <img
                                src={getAdminAssetPreviewUrl(logoUrl, "logo")}
                                alt="Logo Preview"
                                style={{
                                  maxHeight: 120,
                                  maxWidth: "100%",
                                  objectFit: "contain",
                                  marginBottom: 16,
                                  borderRadius: token.borderRadiusSM,
                                }}
                              />

                              {isEditing && (
                                <div>
                                  {" "}
                                  <Button
                                    size="small"
                                    danger
                                    onClick={() =>
                                      form.setFieldValue("logoUrl", "")
                                    }
                                  >
                                    {" "}
                                    Remove Logo{" "}
                                  </Button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <PictureOutlined
                                style={{
                                  fontSize: 36,
                                  color: token.colorTextQuaternary,
                                  marginBottom: 8,
                                }}
                              />
                              <p
                                style={{
                                  color: token.colorTextSecondary,
                                  fontSize: 13,
                                  margin: 0,
                                }}
                              >
                                {" "}
                                No logo uploaded yet
                              </p>
                            </div>
                          )}
                          {isEditing && (
                            <Upload
                              showUploadList={false}
                              beforeUpload={(file) => {
                                handleAssetUpload("logoUrl", file);
                                return Upload.LIST_IGNORE;
                              }}
                            >
                              <Button
                                icon={<CloudUploadOutlined />}
                                style={{ marginTop: 16 }}
                              >
                                {logoUrl ? "Replace Logo" : "Upload Logo"}
                              </Button>
                            </Upload>
                          )}
                        </div>
                      );
                    }}
                  </Form.Item>
                </SectionCard>
              </Col>

              <Col xs={24} md={12}>
                <SectionCard
                  title="Favicon Icon"
                  description="Small browser tab icon (recommended: 32x32 or 48x48)"
                >
                  <DetailField
                    label="Favicon URL"
                    name="faviconUrl"
                    isEditing={isEditing}
                    value={getUrlString(requestData?.faviconUrl)}
                    isLink
                  >
                    <Input
                      placeholder="https://..."
                      prefix={
                        <LinkOutlined
                          style={{ color: token.colorTextQuaternary }}
                        />
                      }
                    />
                  </DetailField>

                  <Form.Item
                    shouldUpdate={(prev, curr) =>
                      prev.faviconUrl !== curr.faviconUrl
                    }
                    noStyle
                  >
                    {({ getFieldValue }) => {
                      const faviconUrl = getFieldValue("faviconUrl");
                      return (
                        <div
                          style={{
                            textAlign: "center",
                            padding: 24,
                            background: token.colorFillAlter,
                            borderRadius: token.borderRadiusLG,
                            border: `1px dashed ${token.colorBorder}`,
                          }}
                        >
                          {faviconUrl ? (
                            <div>
                              <img
                                src={getAdminAssetPreviewUrl(
                                  faviconUrl,
                                  "favicon",
                                )}
                                alt="Favicon Preview"
                                style={{
                                  height: 48,
                                  width: 48,
                                  objectFit: "contain",
                                  marginBottom: 16,
                                  borderRadius: token.borderRadiusSM,
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                }}
                              />

                              {isEditing && (
                                <div>
                                  <Button
                                    size="small"
                                    danger
                                    onClick={() =>
                                      form.setFieldValue("faviconUrl", "")
                                    }
                                  >
                                    Remove Favicon
                                  </Button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <GlobalOutlined
                                style={{
                                  fontSize: 36,
                                  color: token.colorTextQuaternary,
                                  marginBottom: 8,
                                }}
                              />
                              <p
                                style={{
                                  color: token.colorTextSecondary,
                                  fontSize: 13,
                                  margin: 0,
                                }}
                              >
                                {" "}
                                No favicon uploaded yet{" "}
                              </p>
                            </div>
                          )}

                          {isEditing && (
                            <Upload
                              showUploadList={false}
                              beforeUpload={(file) => {
                                handleAssetUpload("faviconUrl", file);
                                return Upload.LIST_IGNORE;
                              }}
                            >
                              <Button
                                icon={<CloudUploadOutlined />}
                                style={{ marginTop: 16 }}
                              >
                                {faviconUrl
                                  ? "Replace Favicon"
                                  : "Upload Favicon"}
                              </Button>
                            </Upload>
                          )}
                        </div>
                      );
                    }}
                  </Form.Item>
                </SectionCard>
              </Col>
            </Row>
          </div>

          {/* TAB 3: Invoice Templates */}
          <div style={{ display: activeTab === "invoices" ? "block" : "none" }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <SectionCard
                  title="Invoice Header Banner"
                  description="Header banner image placed atop generated invoice PDFs"
                >
                  <DetailField
                    label="Header Image URL"
                    name="invoiceSampleHeaderUrl"
                    isEditing={isEditing}
                    value={getUrlString(requestData?.invoiceSampleHeaderUrl)}
                    isLink
                  >
                    <Input
                      placeholder="https://..."
                      prefix={
                        <LinkOutlined
                          style={{ color: token.colorTextQuaternary }}
                        />
                      }
                    />
                  </DetailField>

                  <Form.Item
                    shouldUpdate={(prev, curr) =>
                      prev.invoiceSampleHeaderUrl !==
                      curr.invoiceSampleHeaderUrl
                    }
                    noStyle
                  >
                    {({ getFieldValue }) => {
                      const headerUrl = getFieldValue("invoiceSampleHeaderUrl");
                      return (
                        <div
                          style={{
                            textAlign: "center",
                            padding: 20,
                            background: token.colorFillAlter,
                            borderRadius: token.borderRadiusLG,
                            border: `1px dashed ${token.colorBorder}`,
                          }}
                        >
                          {headerUrl ? (
                            <div>
                              <img
                                src={getAdminAssetPreviewUrl(
                                  headerUrl,
                                  "header",
                                )}
                                alt="Header Preview"
                                style={{
                                  maxHeight: 140,
                                  maxWidth: "100%",
                                  objectFit: "contain",
                                  marginBottom: 12,
                                  borderRadius: token.borderRadiusSM,
                                }}
                              />
                              {isEditing && (
                                <div>
                                  <Button
                                    size="small"
                                    danger
                                    onClick={() =>
                                      form.setFieldValue(
                                        "invoiceSampleHeaderUrl",
                                        "",
                                      )
                                    }
                                  >
                                    Remove Header
                                  </Button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p
                              style={{
                                color: token.colorTextSecondary,
                                fontSize: 13,
                                margin: 0,
                              }}
                            >
                              Default Acting Office header will be used
                            </p>
                          )}

                          {isEditing && (
                            <Upload
                              showUploadList={false}
                              beforeUpload={(file) => {
                                handleAssetUpload(
                                  "invoiceSampleHeaderUrl",
                                  file,
                                );
                                return Upload.LIST_IGNORE;
                              }}
                            >
                              <Button
                                icon={<CloudUploadOutlined />}
                                style={{ marginTop: 14 }}
                              >
                                {headerUrl ? "Replace Header" : "Upload Header"}
                              </Button>
                            </Upload>
                          )}
                        </div>
                      );
                    }}
                  </Form.Item>
                </SectionCard>
              </Col>

              <Col xs={24} md={12}>
                <SectionCard
                  title="Invoice Footer Banner"
                  description="Footer banner placed at the bottom of invoice PDFs"
                >
                  <DetailField
                    label="Footer Image URL"
                    name="invoiceSampleFooterUrl"
                    isEditing={isEditing}
                    value={getUrlString(requestData?.invoiceSampleFooterUrl)}
                    isLink
                  >
                    <Input
                      placeholder="https://..."
                      prefix={
                        <LinkOutlined
                          style={{ color: token.colorTextQuaternary }}
                        />
                      }
                    />
                  </DetailField>
                  <Form.Item
                    shouldUpdate={(prev, curr) =>
                      prev.invoiceSampleFooterUrl !==
                      curr.invoiceSampleFooterUrl
                    }
                    noStyle
                  >
                    {({ getFieldValue }) => {
                      const footerUrl = getFieldValue("invoiceSampleFooterUrl");
                      return (
                        <div
                          style={{
                            textAlign: "center",
                            padding: 20,
                            background: token.colorFillAlter,
                            borderRadius: token.borderRadiusLG,
                            border: `1px dashed ${token.colorBorder}`,
                          }}
                        >
                          {footerUrl ? (
                            <div>
                              <img
                                src={getAdminAssetPreviewUrl(
                                  footerUrl,
                                  "footer",
                                )}
                                alt="Footer Preview"
                                style={{
                                  maxHeight: 140,
                                  maxWidth: "100%",
                                  objectFit: "contain",
                                  marginBottom: 37,
                                  borderRadius: token.borderRadiusSM,
                                }}
                              />
                              {isEditing && (
                                <div>
                                  <Button
                                    size="small"
                                    danger
                                    onClick={() =>
                                      form.setFieldValue(
                                        "invoiceSampleFooterUrl",
                                        "",
                                      )
                                    }
                                  >
                                    {" "}
                                    Remove Footer
                                  </Button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p
                              style={{
                                color: token.colorTextSecondary,
                                fontSize: 13,
                                margin: 0,
                              }}
                            >
                              Default Acting Office footer will be used
                            </p>
                          )}
                          {isEditing && (
                            <Upload
                              showUploadList={false}
                              beforeUpload={(file) => {
                                handleAssetUpload(
                                  "invoiceSampleFooterUrl",
                                  file,
                                );
                                return Upload.LIST_IGNORE;
                              }}
                            >
                              <Button
                                icon={<CloudUploadOutlined />}
                                style={{ marginTop: 14 }}
                              >
                                {footerUrl ? "Replace Footer" : "Upload Footer"}
                              </Button>
                            </Upload>
                          )}
                        </div>
                      );
                    }}
                  </Form.Item>
                </SectionCard>
              </Col>
            </Row>
          </div>

          {/* TAB 4: Uploaded Data & Files */}
          <div style={{ display: activeTab === "files" ? "block" : "none" }}>
            <SectionCard
              title="Client Data Conversion Files"
              description="Spreadsheets submitted by the practice for data migration"
              extra={
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddFileModal(true)}
                  size="small"
                >
                  Upload File
                </Button>
              }
            >
              <Form.List name="dataConversionFiles">
                {(fields, { remove }) => {
                  if (fields.length === 0) {
                    return (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px 0",
                          color: token.colorTextSecondary,
                        }}
                      >
                        <CloudUploadOutlined
                          style={{
                            fontSize: 36,
                            color: token.colorPrimary,
                            marginBottom: 10,
                            opacity: 0.8,
                          }}
                        />
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: token.colorText,
                          }}
                        >
                          No data conversion files uploaded yet
                        </div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          Click "Upload File" above to attach a client
                          spreadsheet.
                        </div>
                      </div>
                    );
                  }

                  const columns = [
                    {
                      title: "S.No",
                      key: "index",
                      width: 60,
                      render: (_: any, __: any, index: number) => index + 1,
                    },
                    {
                      title: "File Name",
                      dataIndex: "name",
                      key: "name",
                      render: (text: string, record: any, index: number) => {
                        const fileItem = normalizeFile(
                          form.getFieldValue(["dataConversionFiles", index]) ||
                            record,
                        );
                        const fileUrl = fileItem.file?.path;
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <FileTextOutlined
                              style={{
                                color: token.colorPrimary,
                                fontSize: 15,
                              }}
                            />
                            {fileUrl ? (
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  fontWeight: 600,
                                  color: token.colorPrimary,
                                }}
                              >
                                {fileItem.name || text}
                              </a>
                            ) : (
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: token.colorText,
                                }}
                              >
                                {fileItem.name || text}
                              </span>
                            )}
                          </div>
                        );
                      },
                    },
                    {
                      title: "Data Type",
                      dataIndex: "type",
                      key: "type",
                      render: (text: string, record: any, index: number) => {
                        const fileItem = normalizeFile(
                          form.getFieldValue(["dataConversionFiles", index]) ||
                            record,
                        );
                        const rawType = fileItem.type || text || "";
                        const opt = CsvTemplateOptions.find(
                          (o) => o.value === rawType || o.label === rawType,
                        );
                        let displayLabel = opt
                          ? opt.label
                          : rawType || "Other Data Import";
                        if (fileItem.subType) {
                          displayLabel += ` - ${fileItem.subType}`;
                        }
                        return (
                          <Tag
                            color="cyan"
                            style={{ borderRadius: 10, fontWeight: 500 }}
                          >
                            {displayLabel}
                          </Tag>
                        );
                      },
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      width: 100,
                      render: (_: any, record: any, index: number) => {
                        const fileItem = normalizeFile(
                          form.getFieldValue(["dataConversionFiles", index]) ||
                            record,
                        );
                        const fileUrl = fileItem.file?.path;
                        return (
                          <Space>
                            {fileUrl && (
                              <Tooltip title="Download File">
                                <Button
                                  size="small"
                                  icon={<DownloadOutlined />}
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                />
                              </Tooltip>
                            )}
                          </Space>
                        );
                      },
                    },
                  ];

                  const tableDataSource = fields.map(({ key, name }) => ({
                    key,
                    name,
                  }));

                  return (
                    <Table
                      dataSource={tableDataSource}
                      columns={columns}
                      pagination={false}
                      size="small"
                      bordered
                      scroll={{ x: "max-content" }}
                    />
                  );
                }}
              </Form.List>
            </SectionCard>
          </div>
        </Form>
      </div>
      {/* Modal upload file */}
      <Modal
        title="Upload Data Conversion File"
        open={addFileModal}
        onCancel={() => setAddFileModal(false)}
        footer={null}
        destroyOnClose
        centered
      >
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontWeight: 600,
                fontSize: 13,
                color: token.colorText,
              }}
            >
              Data Import Type
            </label>
            <Space.Compact style={{ width: "100%" }}>
              <Select
                style={{ width: "100%" }}
                value={newFileType}
                onChange={(val) => {
                  setNewFileType(val);
                  if (val !== CsvTemplateType.Other) setNewFileName("");
                }}
              >
                {CsvTemplateOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {" "}
                    {opt.label}
                  </Option>
                ))}
              </Select>
              <Tooltip title={"Download template"}>
                <Button
                  icon={<DownloadOutlined />}
                  disabled={newFileType === CsvTemplateType.Other}
                  onClick={() => handleDownloadCsvTemplate(newFileType)}
                />
              </Tooltip>
            </Space.Compact>
          </div>
          {newFileType === CsvTemplateType.Other && (
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontWeight: 600,
                  fontSize: 13,
                  color: token.colorText,
                }}
              >
                {" "}
                Custom File Name
              </label>
              <Input
                placeholder="e.g. Historical Trial Balance 2024"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
              />
            </div>
          )}

          <Dragger
            accept=".csv,.xlsx"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              handleAddNewConversionFile(file as File);
              return Upload.LIST_IGNORE;
            }}
            disabled={newFileUploading}
            style={{ padding: "20px 0", background: token.colorFillAlter }}
          >
            {newFileUploading ? (
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            ) : (
              <>
                <CloudUploadOutlined
                  style={{
                    fontSize: 32,
                    color: token.colorPrimary,
                    marginBottom: 8,
                  }}
                />
                <p
                  style={{ margin: 0, fontWeight: 500, color: token.colorText }}
                >
                  Click or drag CSV / Excel file to upload
                </p>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    color: token.colorTextSecondary,
                    fontSize: 12,
                  }}
                >
                  Supported formats: .csv, .xlsx
                </p>
              </>
            )}
          </Dragger>
        </div>
      </Modal>
      <CsvValidationModal
        open={validationModalOpen}
        result={activeValidationResult}
        onClose={() => setValidationModalOpen(false)}
      />
    </PageLayout>
  );
};

export default PracticeDataRequestDetails;
